import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, TrendingUp, MessageSquare } from "lucide-react";
import PendingRatingsList from "@/components/PendingRatingsList";
import RecapPhotosEditor from "@/components/RecapPhotos";
import ConnectStatusCard from "@/components/ConnectStatusCard";
import EditEventDialog from "@/components/EditEventDialog";
import CancelEventDialog from "@/components/CancelEventDialog";
import PushToggle from "@/components/PushToggle";
import ReferralsCard from "@/components/ReferralsCard";
import NonprofitStatusCard from "@/components/NonprofitStatusCard";
import AiFreeToggle from "@/components/AiFreeToggle";
import EventChecklistCard from "@/components/EventChecklistCard";
import FlyerStudioCard from "@/components/FlyerStudioCard";
import EventPredictionsCard from "@/components/EventPredictionsCard";
import OrganizerProGate from "@/components/OrganizerProGate";
import VerifyNudgeBanner from "@/components/VerifyNudgeBanner";
import BoothCategoryEditor from "@/components/BoothCategoryEditor";

function statusClass(s) {
  return {
    pending: "status-pending",
    approved: "status-approved",
    rejected: "status-rejected",
    cancelled: "status-rejected",
    waitlisted: "status-pending",
  }[s] || "status-pending";
}

export default function OrganizerDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // Organizer's saved booth-category preset list — reused across events so
  // recurring markets don't retype Food/Kids/Crafts every time.
  const [presets, setPresets] = useState([]);
  const [presetsBusy, setPresetsBusy] = useState(false);

  const loadPresets = async () => {
    try {
      const { data } = await api.get("/organizer/category-presets");
      setPresets(data.presets || []);
    } catch { /* silent — presets are optional */ }
  };
  useEffect(() => { loadPresets(); }, []);

  const savePresetsFromApproved = async () => {
    // Snapshot every category currently used on approved apps for THIS event
    // and merge into the organizer-wide preset list. Existing presets kept
    // (case-insensitive) so the organizer's cumulative set only grows.
    const usedHere = Array.from(new Set(
      apps.filter((a) => a.status === "approved" && (a.booth_category || "").trim())
          .map((a) => a.booth_category.trim()),
    ));
    if (usedHere.length === 0) {
      toast.error("Categorize at least one approved vendor first.");
      return;
    }
    const merged = [...presets];
    const seen = new Set(presets.map((p) => p.toLowerCase()));
    usedHere.forEach((c) => {
      if (!seen.has(c.toLowerCase())) {
        merged.push(c);
        seen.add(c.toLowerCase());
      }
    });
    setPresetsBusy(true);
    try {
      const { data } = await api.put("/organizer/category-presets",
                                       { presets: merged });
      setPresets(data.presets || []);
      toast.success("Saved as your preset — reuse on any event.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save preset");
    } finally { setPresetsBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events/mine/organizer");
      setEvents(data);
      if (data.length > 0 && !selectedEventId) setSelectedEventId(data[0].id);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    setAppsLoading(true);
    api.get(`/applications/event/${selectedEventId}`).then(({ data }) => setApps(data)).finally(() => setAppsLoading(false));
  }, [selectedEventId]);

  const setStatus = async (appId, status) => {
    try {
      await api.patch(`/applications/${appId}/status`, { status });
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, status } : a)));
      toast.success(`Application ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const setBoothCategory = async (appId, category) => {
    try {
      const { data } = await api.patch(
        `/applications/${appId}/booth-category`,
        { booth_category: category },
      );
      setApps((prev) => prev.map(
        (a) => (a.id === appId ? { ...a, booth_category: data.booth_category } : a),
      ));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save category");
    }
  };

  const toggleShowBoothCategoriesPublicly = async (eventId, next) => {
    try {
      const { data } = await api.patch(`/events/${eventId}`, {
        show_booth_categories: next,
      });
      setEvents((prev) => prev.map(
        (ev) => (ev.id === eventId ? { ...ev, show_booth_categories: data.show_booth_categories } : ev),
      ));
      toast.success(next ? "Categories are now public on the event page." : "Categories hidden from the public event page.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Organizer dashboard</div>
          <h1 className="font-serif text-5xl mt-2">Your events.</h1>
        </div>
        <Link to="/organizer/events/new" data-testid="link-create-event" className="btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New event
        </Link>
      </div>

      {/* Self-serve ID verification nudge — hidden once kyc_approved=true
          or the user has dismissed. Rewards completion visibly on every
          event card via the green IdVerifiedBadge. */}
      <VerifyNudgeBanner user={user} />

      {/* Organizer Pro banner — three states:
           - `isTrialing` → celebratory "Trial active — N days left" banner
           - `trial_available` → "Start 7-day free trial" banner (no card required)
           - otherwise → upgrade banner
          Admin and paid Pro organizers see nothing. */}
      {user && user.role === "organizer" &&
       (user.plan || "free") !== "pro" &&
       !user.is_admin && (
        user.on_trial ? (
          <div
            data-testid="organizer-pro-trial-banner"
            className="mt-6 rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
            style={{
              background: "linear-gradient(135deg, rgba(44,196,217,0.12), rgba(132,204,22,0.12))",
              border: "2px solid var(--avt-teal)",
            }}
          >
            <div className="flex-1">
              <div className="overline" style={{ color: "var(--avt-teal)" }}>
                Trial active · {user.trial_days_remaining} day{user.trial_days_remaining === 1 ? "" : "s"} left
              </div>
              <div className="font-serif text-2xl mt-1">All AI features are unlocked</div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                Enjoy the AI planning suite on us — Flyer Studio, event predictions, vendor matching, PDF contracts.
                When your trial ends, upgrade to keep going at $29/mo (with a reduced 3% platform fee).
              </p>
            </div>
            <Link
              to="/pricing?role=organizer"
              data-testid="organizer-pro-trial-cta"
              className="btn-outline px-5 py-2.5 rounded-full inline-flex items-center gap-2 shrink-0"
            >
              See plans
            </Link>
          </div>
        ) : (
          <div
            data-testid="organizer-pro-banner"
            className="mt-6 rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
            style={{
              background: "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(236,72,153,0.10))",
              border: "2px solid var(--avt-orange)",
            }}
          >
            <div className="flex-1">
              <div className="overline" style={{ color: "var(--avt-orange)" }}>
                {user.trial_available ? "Organizer Pro · 7-day free trial" : "Organizer Pro · $29/mo"}
              </div>
              <div className="font-serif text-2xl mt-1">Unlock the AI planning suite</div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                AI Flyer Studio, event predictions, AI vendor matching, unlimited PDF booth contracts — and a
                <strong> reduced 3% platform fee</strong> on every booth fee you collect (was 5%).
              </p>
            </div>
            <Link
              to="/pricing?role=organizer"
              data-testid="organizer-pro-banner-cta"
              className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 shrink-0"
            >
              <TrendingUp className="h-4 w-4" />
              {user.trial_available ? "Start free trial" : "See Pro"}
            </Link>
          </div>
        )
      )}

      <div className="mt-8">
        <ConnectStatusCard />
      </div>

      <div className="mt-6">
        <PushToggle />
      </div>

      <ReferralsCard variant="organizer" />
      <div className="mt-4">
        <NonprofitStatusCard />
      </div>
      <div className="mt-4">
        <AiFreeToggle initialValue={user?.ai_free} />
      </div>

      {loading ? (
        <div className="mt-16 text-center font-serif text-2xl">Loading…</div>
      ) : events.length === 0 ? (
        <div className="card-warm p-12 rounded-sm text-center mt-12">
          <div className="font-serif text-3xl">No events yet.</div>
          <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>Create your first event and invite vendors to apply.</div>
          <Link to="/organizer/events/new" className="mt-6 inline-block btn-primary px-6 py-3 rounded-full">Create event</Link>
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1 space-y-2" data-testid="events-list">
            {events.map((e) => (
              <button key={e.id} data-testid={`event-tab-${e.id}`} onClick={() => setSelectedEventId(e.id)}
                      className={`w-full text-left p-4 rounded-sm border transition ${selectedEventId === e.id ? "border-[color:var(--terracotta)] bg-white" : "border-[color:var(--border-warm)] bg-[color:var(--sand)]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-serif text-xl truncate">{e.title}</div>
                  {e.moderation_status === "pending_review" && (
                    <span data-testid={`event-mod-pending-${e.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}>Pending</span>
                  )}
                  {e.moderation_status === "hidden" && (
                    <span data-testid={`event-mod-hidden-${e.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}>Hidden</span>
                  )}
                  {e.moderation_status === "cancelled" && (
                    <span data-testid={`event-mod-cancelled-${e.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}>Cancelled</span>
                  )}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{e.date} · {e.city}</div>
              </button>
            ))}
          </aside>
          <section className="lg:col-span-3">
            {(() => {
              const selectedEvent = events.find((ev) => ev.id === selectedEventId);
              if (!selectedEvent) return null;
              const isPast = selectedEvent.date && selectedEvent.date < new Date().toISOString().slice(0, 10);
              const isCancelled = selectedEvent.moderation_status === "cancelled";
              return (
                <>
                  <div className="mb-6 flex items-start justify-between gap-3 flex-wrap" data-testid="event-actions-row">
                    <div>
                      <div className="overline" style={{ color: "var(--terracotta)" }}>
                        {selectedEvent.title}
                      </div>
                      <div className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                        {selectedEvent.date} · {selectedEvent.city}
                        {selectedEvent.booth_fee > 0 ? ` · $${selectedEvent.booth_fee.toFixed(2)} booth fee` : " · Free booth"}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!isCancelled && !isPast && (
                        <Link
                          to={`/organizer/events/${selectedEvent.id}/messages`}
                          data-testid={`event-messages-btn-${selectedEvent.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors"
                          style={{
                            borderColor: "var(--avt-purple)",
                            color: "var(--avt-purple)",
                          }}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          Messages &amp; matches
                        </Link>
                      )}
                      {!isCancelled && !isPast && (
                        <Button
                          data-testid={`event-edit-btn-${selectedEvent.id}`}
                          variant="outline" size="sm"
                          onClick={() => setEditOpen(true)}
                          className="rounded-full"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                        </Button>
                      )}
                      {!isCancelled && (
                        <Button
                          data-testid={`event-cancel-btn-${selectedEvent.id}`}
                          variant="outline" size="sm"
                          onClick={() => setCancelOpen(true)}
                          className="rounded-full"
                          style={{ borderColor: "rgba(31,93,52,0.4)", color: "var(--terracotta)" }}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          {apps.length === 0 ? "Delete" : "Cancel event"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isCancelled && (
                    <div
                      className="mb-6 rounded-sm p-4 border text-sm"
                      style={{
                        backgroundColor: "rgba(31,93,52,0.06)",
                        borderColor: "rgba(31,93,52,0.3)",
                        color: "var(--ink-soft)",
                      }}
                      data-testid={`event-cancelled-banner-${selectedEvent.id}`}
                    >
                      This event was cancelled{selectedEvent.cancelled_at ? ` on ${selectedEvent.cancelled_at.slice(0, 10)}` : ""}. It’s hidden from public listings and all applicants have been notified.
                    </div>
                  )}

                  {isPast && !isCancelled && (
                    <div className="mb-8 card-warm rounded-sm p-6" data-testid="recap-editor-wrapper">
                      <RecapPhotosEditor
                        event={selectedEvent}
                        onChange={(updated) => setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))}
                      />
                    </div>
                  )}

                  {/* Planning cards — hidden for cancelled events, shown for future
                      + past events (past organizers may still want the flyer for
                      recap posts). Checklist is FREE for every organizer; the AI
                      flyer + predictions live behind Organizer Pro. */}
                  {!isCancelled && (
                    <div className="mb-8 grid gap-6 lg:grid-cols-2" data-testid="planning-cards">
                      <EventChecklistCard eventId={selectedEvent.id} />
                      <OrganizerProGate
                        feature="AI Flyer Studio"
                        pitch="Nano Banana designs a share-ready flyer for every event you host — one tap, unlimited generations."
                        perks={[
                          "Unlimited AI-generated flyers per event",
                          "Auto share-caption + 6-platform social share sheet",
                          "Custom URL landing page for every flyer",
                        ]}
                        testIdKey="flyer-studio"
                      >
                        <FlyerStudioCard eventId={selectedEvent.id} />
                      </OrganizerProGate>
                    </div>
                  )}
                  {!isCancelled && (
                    <div className="mb-8" data-testid="predictions-wrapper">
                      <OrganizerProGate
                        feature="Event Predictions"
                        pitch="Get an AI forecast of weather, attendance, and revenue for every event you host — plus one-tap sharing of the tiles with your vendors."
                        perks={[
                          "16-day weather forecast via Open-Meteo",
                          "Attendance range tuned to your category + city",
                          "Revenue estimate — total AND per-vendor",
                          "Selectable sharing (pick what vendors see)",
                        ]}
                        testIdKey="predictions"
                      >
                        <EventPredictionsCard eventId={selectedEvent.id} eventDate={selectedEvent.date} />
                      </OrganizerProGate>
                    </div>
                  )}
                </>
              );
            })()}
            <div className="overline mb-4" style={{ color: "var(--terracotta)" }}>Applications</div>
            {appsLoading ? (
              <div className="py-10 text-center font-serif text-2xl">Loading…</div>
            ) : apps.length === 0 ? (
              <div className="card-warm p-8 rounded-sm text-center">
                <div className="font-serif text-2xl">No applications yet for this event.</div>
              </div>
            ) : (
              (() => {
                const selectedEvent = events.find((ev) => ev.id === selectedEventId);
                const selEv = selectedEvent;
                const maxV = parseInt(selEv?.max_vendors || 0, 10) || 0;
                const approvedApps = apps.filter((a) => a.status === "approved");
                const approvedCount = approvedApps.length;
                const waitlistedApps = apps.filter((a) => a.status === "waitlisted");
                const nonApprovedMainApps = apps.filter(
                  (a) => a.status !== "waitlisted" && a.status !== "approved",
                );
                const isFull = maxV > 0 && approvedCount >= maxV;
                // Datalist of every booth category already used on this event
                // PLUS the organizer's saved presets so the editor can
                // auto-complete instead of re-typing.
                const approvedCategorySuggestions = Array.from(new Set([
                  ...approvedApps
                    .map((a) => (a.booth_category || "").trim())
                    .filter(Boolean),
                  ...presets,
                ])).sort();
                // Group approved apps by booth_category; uncategorized fall
                // under "Miscellaneous". Preserves original approval order
                // within each bucket.
                const approvedGroupsMap = new Map();
                approvedApps.forEach((a) => {
                  const key = (a.booth_category || "").trim() || "Miscellaneous";
                  if (!approvedGroupsMap.has(key)) approvedGroupsMap.set(key, []);
                  approvedGroupsMap.get(key).push(a);
                });
                // Sort groups alphabetically, but push Miscellaneous last so
                // the named categories lead the eye down the page.
                const approvedGroups = Array.from(approvedGroupsMap.entries())
                  .sort(([a], [b]) => {
                    if (a === "Miscellaneous") return 1;
                    if (b === "Miscellaneous") return -1;
                    return a.localeCompare(b);
                  });
                const renderCard = (a, opts = {}) => {
                  // Vendor profile lives at `/vendors/{slug}`. Only linkable
                  // when we have a slug (freshly registered vendors may not
                  // yet — those stay as plain text).
                  const profileHref = a.vendor_slug ? `/vendors/${a.vendor_slug}` : null;
                  const headerName = profileHref ? (
                    <Link
                      to={profileHref}
                      data-testid={`inbox-app-${a.id}-profile-link`}
                      className="hover:underline"
                      style={{ color: "var(--avt-purple)" }}
                      title="View vendor profile"
                    >
                      {a.vendor_name}
                    </Link>
                  ) : (
                    <span>{a.vendor_name}</span>
                  );
                  return (
                  <div key={a.id} data-testid={`inbox-app-${a.id}`} className="card-warm rounded-sm p-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3">
                        {a.vendor_avatar_url && profileHref ? (
                          <Link to={profileHref}
                                data-testid={`inbox-app-${a.id}-avatar-link`}
                                className="flex-shrink-0"
                                title="View vendor profile">
                            <img src={a.vendor_avatar_url} alt=""
                                 className="h-14 w-14 rounded-full object-cover ring-2"
                                 style={{ ringColor: "var(--avt-purple)" }} />
                          </Link>
                        ) : a.vendor_avatar_url ? (
                          <img src={a.vendor_avatar_url} alt=""
                               className="h-14 w-14 rounded-full object-cover" />
                        ) : null}
                        <div>
                          <div className="font-serif text-2xl">{headerName}</div>
                          <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                            {a.vendor_business || "—"} · Booth: {a.booth_size}
                          </div>
                          {a.vendor_city && (
                            <div className="text-[11px] mt-0.5" style={{ color: "var(--ink-soft)" }}>
                              {a.vendor_city}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className={`status-badge ${statusClass(a.status)}`}>{a.status}</span>
                    </div>
                    {a.status === "approved" && (
                      <div className="mt-3">
                        <BoothCategoryEditor
                          applicationId={a.id}
                          currentCategory={a.booth_category}
                          suggestions={approvedCategorySuggestions}
                          onChange={(next) => setBoothCategory(a.id, next)}
                        />
                      </div>
                    )}
                    <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                      <div><span className="overline block" style={{ color: "var(--ink-soft)" }}>Pitch</span>{a.pitch}</div>
                      <div><span className="overline block" style={{ color: "var(--ink-soft)" }}>Products</span>{a.products}</div>
                    </div>
                    {a.occurrence_dates && a.occurrence_dates.length > 0 && selectedEvent?.is_recurring && (
                      <div className="mt-4" data-testid={`app-occurrences-${a.id}`}>
                        <span className="overline block mb-1" style={{ color: "var(--ink-soft)" }}>
                          Attending · {a.occurrence_dates.length}/{selectedEvent.occurrences?.length || 0} dates
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {a.occurrence_dates.slice(0, 20).map((d) => (
                            <span key={d} className="text-[11px] px-2 py-0.5 rounded"
                              style={{ backgroundColor: "var(--paper-warm)",
                                       border: "1px solid var(--sand)",
                                       color: "var(--ink-soft)" }}>
                              {formatShortDate(d)}
                            </span>
                          ))}
                          {a.occurrence_dates.length > 20 && (
                            <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                              +{a.occurrence_dates.length - 20} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="mt-5 flex gap-2 flex-wrap">
                      {a.status === "waitlisted" && (
                        <Button size="sm" data-testid={`promote-${a.id}`} onClick={() => setStatus(a.id, "pending")} className="btn-primary rounded-full">
                          Promote from waitlist
                        </Button>
                      )}
                      {a.status !== "cancelled" && (
                        <>
                          <Button size="sm" data-testid={`approve-${a.id}`} onClick={() => setStatus(a.id, "approved")} className="btn-primary rounded-full">Approve</Button>
                          <Button size="sm" variant="outline" data-testid={`reject-${a.id}`} onClick={() => setStatus(a.id, "rejected")} className="rounded-full">Reject</Button>
                        </>
                      )}
                      <Link to={`/events/${a.event_id}`} className="text-sm underline self-center ml-auto">View event →</Link>
                    </div>
                  </div>
                  );
                };
                return (
                  <div className="space-y-6" data-testid="applications-inbox">
                    {maxV > 0 && (
                      <div
                        data-testid="capacity-chip"
                        className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1.5 border"
                        style={{
                          borderColor: isFull ? "rgba(31,93,52,0.4)" : "rgba(111,125,85,0.35)",
                          backgroundColor: isFull ? "rgba(31,93,52,0.08)" : "rgba(111,125,85,0.08)",
                          color: isFull ? "var(--terracotta)" : "var(--moss)",
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "currentColor" }} />
                        {approvedCount} / {maxV} vendors approved
                        {isFull && <span className="opacity-80"> · new applicants land on the waitlist</span>}
                        {!isFull && waitlistedApps.length > 0 && <span className="opacity-80"> · {waitlistedApps.length} on waitlist</span>}
                      </div>
                    )}
                    <div className="space-y-4">
                      {nonApprovedMainApps.map((a) => renderCard(a))}
                    </div>
                    {approvedApps.length > 0 && (
                      <div data-testid="approved-section" className="pt-2 space-y-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="overline" style={{ color: "var(--moss)" }}>
                            Approved · {approvedCount}
                          </div>
                          <div className="inline-flex items-center gap-4 flex-wrap">
                            <button
                              type="button"
                              onClick={savePresetsFromApproved}
                              disabled={presetsBusy}
                              data-testid="save-category-preset-btn"
                              className="text-xs font-bold underline decoration-dotted underline-offset-4 hover:opacity-70 disabled:opacity-40"
                              style={{ color: "var(--avt-purple)" }}
                              title="Save the categories used on this event so you can reuse them next time"
                            >
                              Save as my preset
                            </button>
                            <label
                              className="inline-flex items-center gap-2 text-xs cursor-pointer select-none"
                              style={{ color: "var(--ink-soft)" }}
                              data-testid="show-categories-toggle"
                            >
                              <input
                                type="checkbox"
                                checked={!!selEv?.show_booth_categories}
                                onChange={(e) => toggleShowBoothCategoriesPublicly(selEv.id, e.target.checked)}
                                data-testid="show-categories-toggle-input"
                                className="h-4 w-4 accent-[color:var(--avt-purple)]"
                              />
                              Show categories on public event page
                            </label>
                          </div>
                        </div>
                        {approvedGroups.map(([label, groupApps]) => (
                          <div
                            key={label}
                            data-testid={`approved-group-${label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <div
                                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                                style={{
                                  background: label === "Miscellaneous"
                                    ? "rgba(0,0,0,0.05)"
                                    : "rgba(107,47,186,0.14)",
                                  color: label === "Miscellaneous"
                                    ? "var(--ink-soft)"
                                    : "var(--avt-purple)",
                                }}
                              >
                                {label} · {groupApps.length}
                              </div>
                            </div>
                            <div className="space-y-4">
                              {groupApps.map((a) => renderCard(a))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {waitlistedApps.length > 0 && (
                      <div data-testid="waitlist-section" className="pt-2">
                        <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>
                          Waitlist · {waitlistedApps.length}
                        </div>
                        <p className="text-xs mb-4" style={{ color: "var(--ink-soft)" }}>
                          These vendors applied after your event filled up. Promote anyone here to move them into the pending queue — we&apos;ll ping you whenever an approved vendor is rejected or cancelled so you can promote whoever you want.
                        </p>
                        <div className="space-y-4">
                          {waitlistedApps.map((a) => renderCard(a))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </section>
        </div>
      )}

      <PendingRatingsList />

      {(() => {
        const selectedEvent = events.find((ev) => ev.id === selectedEventId);
        if (!selectedEvent) return null;
        return (
          <>
            <EditEventDialog
              event={selectedEvent}
              open={editOpen}
              onOpenChange={setEditOpen}
              onSaved={(updated) =>
                setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
              }
            />
            <CancelEventDialog
              event={selectedEvent}
              open={cancelOpen}
              onOpenChange={setCancelOpen}
              onDone={(res) => {
                if (res.mode === "deleted") {
                  setEvents((prev) => prev.filter((e) => e.id !== selectedEvent.id));
                  setSelectedEventId(null);
                  setApps([]);
                } else {
                  setEvents((prev) =>
                    prev.map((e) =>
                      e.id === selectedEvent.id
                        ? { ...e, moderation_status: "cancelled", cancelled_at: new Date().toISOString() }
                        : e
                    )
                  );
                  setApps((prev) => prev.map((a) => ({ ...a, status: "cancelled" })));
                }
              }}
            />
          </>
        );
      })()}
    </div>
  );
}

function formatShortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return iso;
  }
}
